import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { errorMessageFromBody, isNetworkError, NETWORK_ERROR_MESSAGE } from '../../../services/api';

function debounce<T extends (...args: any[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

export interface PlatformState {
  id: string;
  name: string;
  icon: string;
  url: string;
  connected: boolean;
  scanning: boolean;
  oauth_code?: string;
  instagram_url?: string;  // Auto-detected Instagram profile URL
  instagram_username?: string;  // Auto-detected Instagram username
  instagram_checked?: boolean;  // True after backend has checked for linked Instagram
}

export interface PermissionState {
  reachAnalytics: boolean;
  audienceDemographics: boolean;
  commentVisibility: boolean;
  watchTimeSubscriptions: boolean;
}

export interface CompetitorEntry {
  name: string;
  url: string;
  focus: string;
}

export type StrategyMode = 'regular' | 'campaign' | 'hybrid';

export const INITIAL_PLATFORMS: PlatformState[] = [
  { id: 'linkedin', name: 'LinkedIn', icon: 'ri-linkedin-box-line', url: '', connected: false, scanning: false },
  { id: 'youtube', name: 'YouTube', icon: 'ri-youtube-line', url: '', connected: false, scanning: false },
  { id: 'facebook', name: 'Facebook & Instagram', icon: 'ri-facebook-box-line', url: '', connected: false, scanning: false },
];

export const TONE_OPTIONS = [
  'Professional', 'Conversational', 'Corporate', 'Educational',
  'Friendly', 'Technical', 'Authority-Driven', 'Minimal', 'Bold', 'Analytical',
];

export const ARCHETYPE_OPTIONS = [
  'Thought Leader', 'Community-Focused', 'Innovative', 'Premium',
  'Approachable', 'Insight-Led', 'Educational', 'Disruptive',
];

export const PLATFORM_PRIORITY_ORDER = [
  { id: 'linkedin', name: 'LinkedIn', icon: 'ri-linkedin-box-line' },
  { id: 'instagram', name: 'Instagram', icon: 'ri-instagram-line' },
  { id: 'youtube', name: 'YouTube', icon: 'ri-youtube-line' },
  { id: 'facebook', name: 'Facebook', icon: 'ri-facebook-box-line' },
];

export const PLATFORM_TYPE_OPTIONS = [
  'Instagram',
  'LinkedIn',
  'YouTube',
  'Facebook',
];

// Observance picks allowed per cycle; the server sends the user's own cap
// (lower on plans with fewer posts) with the observance list.
export const MAX_OBSERVANCE_PICKS = 4;

// ─────────────────────────────────────────────────────────────────────────────
// URL Validation — mirrors backend utils/social_url_validator.py patterns
// ─────────────────────────────────────────────────────────────────────────────

// Map from SMM platform id (used in INITIAL_PLATFORMS) to canonical backend key
const PLATFORM_ID_TO_KEY: Record<string, string> = {
  linkedin: 'linkedin',
  youtube: 'youtube',
  facebook: 'meta_facebook',   // covers both Facebook & Instagram card
  instagram: 'meta_instagram',
};

export const PLATFORM_URL_PATTERNS: Record<string, RegExp> = {
  youtube: /^https:\/\/(www\.)?youtube\.com\/.*$/i,
  linkedin: /^https:\/\/(www\.)?(in\.)?linkedin\.com\/.*$/i,
  meta_facebook: /^https:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9_\.]+(\/)?(\?.*)?$/i,
  meta_instagram: /^https:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_\.]+(\/)?(\?.*)?$/i,
  twitter: /^https?:\/\/(www\.)?(twitter|x)\.com\/[\w]{1,50}(\/[^?]*)?(\?.*)?$/i,
};

export function validatePlatformUrl(platformKey: string, url: string): boolean {
  if (!url || !url.trim()) return false;
  const pattern = PLATFORM_URL_PATTERNS[platformKey];
  if (!pattern) return false;
  return pattern.test(url.trim());
}

// ─────────────────────────────────────────────────────────────────────────────
// Channel Detection types
// ─────────────────────────────────────────────────────────────────────────────

export interface ChannelDetectionItem {
  platform: string;
  profile_url: string | null;
  prefilled: boolean;
  requires_manual_entry: boolean;
  is_authenticated: boolean;
  authenticated_via: string | null;
  source: string | null;
}

export interface ChannelDetectionResponse {
  status: string;
  business_id: string;
  channels: ChannelDetectionItem[];
}

// ─────────────────────────────────────────────────────────────────────────────
// API helpers — match pattern in src/services/actionPlanService.ts
// ─────────────────────────────────────────────────────────────────────────────

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function saveStep1(
  userId: string,
  stepData: { platforms: PlatformState[]; permissions: PermissionState }
): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE_URL}/smm/onboarding/step1`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId,
      platforms: stepData.platforms
        .filter(p => p.connected)
        .map(p => ({ platform: p.id, profile_url: p.url, oauth_code: p.oauth_code })),
      permissions: {
        reach_analytics: stepData.permissions.reachAnalytics,
        audience_demographics: stepData.permissions.audienceDemographics,
        comment_visibility: stepData.permissions.commentVisibility,
        watch_time_subscriptions: stepData.permissions.watchTimeSubscriptions,
      },
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(errorMessageFromBody(error, response.status));
  }
  return response.json();
}

async function saveStep2(
  userId: string,
  stepData: {
    selectedTones: string[];
    selectedArchetypes: string[];
    platformPriority: string[];
    competitors: CompetitorEntry[];
  }
): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE_URL}/smm/onboarding/step2`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId,
      communication_tones: stepData.selectedTones,
      brand_personalities: stepData.selectedArchetypes,
      platform_priority_order: stepData.platformPriority,
      competitor_references: stepData.competitors.map(c => ({
        name: c.name,
        url: c.url,
        platform_type: c.focus,
      })),
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(errorMessageFromBody(error, response.status));
  }
  return response.json();
}

async function saveStep3(
  userId: string,
  stepData: {
    strategyMode: StrategyMode;
    allocationPercent: number;
    observanceEnabled: boolean;
    selectedObservances: string[];
    country: string;
    region: string;
  }
): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE_URL}/smm/onboarding/step3`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      user_id: userId,
      planning_approach: stepData.strategyMode,
      allocation_percent: stepData.allocationPercent,
      observance_enabled: stepData.observanceEnabled,
      selected_observances: stepData.selectedObservances,
      country: stepData.country,
      region: stepData.region || null,
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(errorMessageFromBody(error, response.status));
  }
  return response.json();
}

async function completeOnboarding(userId: string): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE_URL}/smm/onboarding/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ user_id: userId }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(errorMessageFromBody(error, response.status));
  }
  return response.json();
}

export async function fetchOnboardingStatus(
  userId: string
): Promise<{ is_initialized: boolean; current_step: number | null }> {
  const response = await fetch(
    `${API_BASE_URL}/smm/onboarding/status?user_id=${encodeURIComponent(userId)}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(errorMessageFromBody(error, response.status));
  }
  const data = await response.json();
  // Backend returns StandardResponse: { status, message, user_id, data: { is_initialized, current_step } }
  return data.data;
}

function mapDropdownToPlatformKey(focus: string): string {
  const p = focus.toLowerCase().trim();
  if (p.includes('facebook')) return 'meta_facebook';
  if (p.includes('instagram')) return 'meta_instagram';
  if (p.includes('linkedin')) return 'linkedin';
  if (p.includes('youtube')) return 'youtube';
  if (p.includes('twitter') || p.includes('x')) return 'twitter';
  return p;
}


export function useContentOnboardingState() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);

  // Step 1: Social Channels
  const [ecosystemUrl, setEcosystemUrl] = useState('');
  const [urlScanning, setUrlScanning] = useState(false);
  const [urlScanned, setUrlScanned] = useState(false);
  const [platforms, setPlatforms] = useState<PlatformState[]>(INITIAL_PLATFORMS);

  // Channel detection — prefill + auth state from backend
  const [channelDetection, setChannelDetection] = useState<ChannelDetectionItem[]>([]);
  const [detectionLoading, setDetectionLoading] = useState(false);

  // Per-platform URL validation state
  const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});
  const [urlDirty, setUrlDirty] = useState<Record<string, boolean>>({});

  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const isHydrated = useRef(false);
  // Set when the status check or the saved draft couldn't be loaded; the page
  // shows it with a Retry instead of silently restarting the wizard.
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [draftSaveFailed, setDraftSaveFailed] = useState(false);

  const [permissionsExpanded, setPermissionsExpanded] = useState(false);
  const [permissions, setPermissions] = useState<PermissionState>({
    reachAnalytics: false,
    audienceDemographics: false,
    commentVisibility: false,
    watchTimeSubscriptions: false,
  });

  // Step 2: Brand Communication
  const [selectedTones, setSelectedTones] = useState<string[]>([]);
  const [selectedArchetypes, setSelectedArchetypes] = useState<string[]>([]);
  const [platformPriority, setPlatformPriority] = useState(() => {
    if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
      return PLATFORM_PRIORITY_ORDER.map(p => p.id);
    }
    return ['youtube'];
  });
  const [competitors, setCompetitors] = useState<CompetitorEntry[]>([
    { name: '', url: '', focus: '' },
  ]);

  // Step 3: Content Planning
  const [strategyMode, setStrategyMode] = useState<StrategyMode>('regular');
  const [planCap, setPlanCap] = useState(35); // Default to Silver (35%)
  const [planName, setPlanName] = useState('Silver');
  const [allocationPercent, setAllocationPercent] = useState(100); // 100% regular on start
  const [observanceEnabled, setObservanceEnabled] = useState(false);
  const [selectedObservances, setSelectedObservances] = useState<string[]>([]);
  const [maxObservances, setMaxObservances] = useState<number>(MAX_OBSERVANCE_PICKS);
  const [observanceError, setObservanceError] = useState('');
  const [country, setCountry] = useState('IN');
  const [region, setRegion] = useState('');

  // Hydrate draft state and fetch channel detection on mount
  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      setIsStatusLoading(false);
      return;
    }

    async function initializeOnboarding() {
      try {
        setIsStatusLoading(true);
        setLoadError('');
        const headers = getAuthHeaders();

        // 1. Fetch channel detection
        setDetectionLoading(true);
        let detectedChannels: ChannelDetectionItem[] = [];
        try {
          const detRes = await fetch(`${API_BASE_URL}/smm/channel-detection?business_id=${encodeURIComponent(userId)}`, { headers });
          if (detRes.ok) {
            const detData = await detRes.json();
            detectedChannels = detData.channels || [];
            setChannelDetection(detectedChannels);
          }
        } catch (detErr) {
          console.warn('[SMM] channel-detection fetch failed:', detErr);
        } finally {
          setDetectionLoading(false);
        }

        // 2. Fetch onboarding status (initialized check). If this fails we
        // can't tell whether setup is already done, so stop rather than show
        // the wizard to a user who has finished it.
        const statusRes = await fetch(`${API_BASE_URL}/smm/onboarding/status?user_id=${encodeURIComponent(userId)}`, { headers });
        if (!statusRes.ok) {
          const errBody = await statusRes.json().catch(() => ({}));
          throw new Error(errorMessageFromBody(errBody, statusRes.status));
        }
        const statusData = await statusRes.json();
        if (statusData?.data?.is_initialized) {
          setIsComplete(true);
          setIsStatusLoading(false);
          return;
        }

        // 3. Fetch draft state
        const draftRes = await fetch(`${API_BASE_URL}/smm/onboarding/draft?business_id=${encodeURIComponent(userId)}`, { headers });
        if (draftRes.ok) {
          const draft = await draftRes.json();

          // Cap current step to 3 (since onboarding only has 3 steps). 
          // If the draft claims to be complete or step 4, but we reached here, 
          // it means the final onboarding completion call failed. Force resume at step 3.
          const resumeStep = Math.min(draft.current_step || 1, 3);
          setCurrentStep(resumeStep);

          const stepData = draft.step_data || {};

          // Populate step 1 data from draft (or fallback to channel detection)
          const step1 = stepData['1'];
          if (step1) {
            if (Array.isArray(step1.platforms)) {
              setPlatforms(prev => prev.map(p => {
                const draftPlatform = step1.platforms.find((dp: any) => dp && dp.platform === p.id);
                if (draftPlatform) {
                  let finalUrl = draftPlatform.profile_url || '';
                  if (!finalUrl && detectedChannels.length > 0) {
                    const key = PLATFORM_ID_TO_KEY[p.id] || p.id;
                    const detected = detectedChannels.find(c => c.platform === key);
                    if (detected?.profile_url) {
                      finalUrl = detected.profile_url;
                    }
                  }
                  return {
                    ...p,
                    url: finalUrl,
                    connected: draftPlatform.connected || false,
                    oauth_code: draftPlatform.oauth_code,
                  };
                }
                
                // Fallback to detection if platform isn't in draft
                if (detectedChannels.length > 0) {
                  const key = PLATFORM_ID_TO_KEY[p.id] || p.id;
                  const detected = detectedChannels.find(c => c.platform === key);
                  if (detected?.profile_url) {
                    return { ...p, url: detected.profile_url };
                  }
                }
                
                return p;
              }));
            }
            if (step1.permissions) {
              setPermissions({
                reachAnalytics: step1.permissions.reach_analytics || false,
                audienceDemographics: step1.permissions.audience_demographics || false,
                commentVisibility: step1.permissions.comment_visibility || false,
                watchTimeSubscriptions: step1.permissions.watch_time_subscriptions || false,
              });
            }
          } else if (detectedChannels.length > 0) {
            // Pre-populate platform URL fields from detection result if draft step 1 is empty
            setPlatforms(prev =>
              prev.map(p => {
                const key = PLATFORM_ID_TO_KEY[p.id];
                const detected = detectedChannels.find(c => c.platform === key);
                if (detected?.profile_url) {
                  return { ...p, url: detected.profile_url };
                }
                return p;
              })
            );
          }

          // Populate step 2 data
          const step2 = stepData['2'];
          if (step2) {
            if (Array.isArray(step2.communication_tones)) setSelectedTones(step2.communication_tones);
            if (Array.isArray(step2.brand_personalities)) setSelectedArchetypes(step2.brand_personalities);
            if (Array.isArray(step2.platform_priority_order)) setPlatformPriority(step2.platform_priority_order);
            if (Array.isArray(step2.competitor_references)) {
              setCompetitors(step2.competitor_references.map((c: any) => ({
                name: c?.name || '',
                url: c?.url || '',
                focus: c?.platform_type || '',
              })));
            }
          }

          // Populate step 3 data
          const step3 = stepData['3'];
          if (step3) {
            if (step3.planning_approach) setStrategyMode(step3.planning_approach);
            if (step3.allocation_percent !== undefined) setAllocationPercent(step3.allocation_percent);
            if (step3.observance_enabled !== undefined) setObservanceEnabled(step3.observance_enabled);
            if (Array.isArray(step3.selected_observances)) setSelectedObservances(step3.selected_observances);
            if (step3.country) setCountry(step3.country);
            if (step3.region !== undefined) setRegion(step3.region || '');
          }
        } else {
          // The backend creates an empty draft when none exists, so a failure
          // here is a real error. Restarting at Step 1 would hide the user's
          // saved answers (and autosave would then overwrite them).
          const errBody = await draftRes.json().catch(() => ({}));
          throw new Error(errorMessageFromBody(errBody, draftRes.status));
        }
        isHydrated.current = true;
      } catch (err: any) {
        console.error('Initialization / hydration failed:', err);
        // Not hydrated, so autosave stays off and can't overwrite the saved draft.
        setLoadError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Could not load your setup.');
      } finally {
        setIsStatusLoading(false);
      }
    }

    initializeOnboarding();
  }, [loadAttempt]);

  const retryLoad = () => setLoadAttempt(n => n + 1);

  // Debounced autosave implementation
  const autosaveStep = async (step: number, data: any) => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/smm/onboarding/draft/step/${step}?business_id=${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Autosave failed (${res.status})`);
      setDraftSaveFailed(false);
    } catch (err) {
      console.error('Autosave failed:', err);
      setDraftSaveFailed(true);
    }
  };

  const debouncedAutosave = useMemo(
    () => debounce((step: number, data: any) => autosaveStep(step, data), 500),
    []
  );

  useEffect(() => {
    if (!isHydrated.current) return;

    let data: any = null;
    if (currentStep === 1) {
      data = {
        platforms: platforms.map(p => ({
          platform: p.id,
          profile_url: p.url,
          oauth_code: p.oauth_code,
          connected: p.connected,
        })),
        permissions: {
          reach_analytics: permissions.reachAnalytics,
          audience_demographics: permissions.audienceDemographics,
          comment_visibility: permissions.commentVisibility,
          watch_time_subscriptions: permissions.watchTimeSubscriptions,
        }
      };
    } else if (currentStep === 2) {
      data = {
        communication_tones: selectedTones,
        brand_personalities: selectedArchetypes,
        platform_priority_order: platformPriority,
        competitor_references: competitors.map(c => ({
          name: c.name,
          url: c.url,
          platform_type: c.focus,
        })),
      };
    } else if (currentStep === 3) {
      data = {
        planning_approach: strategyMode,
        allocation_percent: allocationPercent,
        observance_enabled: observanceEnabled,
        selected_observances: selectedObservances,
        country: country,
        region: region,
      };
    }

    if (data) {
      debouncedAutosave(currentStep, data);
    }
  }, [
    currentStep,
    platforms,
    permissions,
    selectedTones,
    selectedArchetypes,
    platformPriority,
    competitors,
    strategyMode,
    allocationPercent,
    observanceEnabled,
    selectedObservances,
    country,
    region
  ]);


  // Fetch subscription plan to set cap dynamically
  useEffect(() => {
    const fetchUserPlan = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      try {
        const token = localStorage.getItem('access_token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const subRes = await fetch(`${API_BASE_URL}/subscription/me/${userId}`, { headers });
        if (subRes.ok) {
          const subData = await subRes.json();
          if (subData && subData.plan_id) {
            const plansRes = await fetch(`${API_BASE_URL}/subscription/plans`, { headers });
            if (plansRes.ok) {
              const plansData = await plansRes.json();
              const matchedPlan = plansData.find((p: any) => p.id === subData.plan_id);
              if (matchedPlan) {
                const code = matchedPlan.plan_code.toUpperCase();
                if (code === 'BASIC' || code === 'BRONZE') {
                  setPlanCap(25);
                  setPlanName('Bronze');
                } else if (code === 'PRO' || code === 'SILVER') {
                  setPlanCap(35);
                  setPlanName('Silver');
                } else if (code === 'ENTERPRISE' || code === 'GOLD') {
                  setPlanCap(45);
                  setPlanName('Gold');
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch plan cap:', err);
      }
    };
    fetchUserPlan();
  }, []);

  // Update allocation percent when plan cap is resolved or strategy mode is hybrid
  useEffect(() => {
    if (strategyMode === 'hybrid') {
      setAllocationPercent(planCap);
    }
  }, [planCap, strategyMode]);

  // API call state
  const [stepError, setStepError] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const scanTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Step 1 Actions ----
  const handleEcosystemUrlChange = (value: string) => {
    setEcosystemUrl(value);
    setUrlScanned(false);
    if (scanTimer.current) clearTimeout(scanTimer.current);
    if (!value.trim()) {
      setUrlScanning(false);
      return;
    }
    setUrlScanning(true);
    scanTimer.current = setTimeout(() => {
      setUrlScanning(false);
      setUrlScanned(true);
      // Auto-connect first two platforms as mock detected
      setPlatforms(prev => prev.map((p, i) => (i < 2 ? { ...p, url: `https://${p.name.toLowerCase()}.com/${value.replace(/[^a-zA-Z0-9]/g, '')}`, scanning: false } : p)));
    }, 1500);
  };

  const handlePlatformUrlChange = (id: string, value: string) => {
    setPlatforms(prev => prev.map(p => (p.id === id ? { ...p, url: value } : p)));
    setUrlDirty(prev => ({ ...prev, [id]: true }));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next.platforms;
      return next;
    });
    // Debounced validation — validate immediately on explicit set
    const key = PLATFORM_ID_TO_KEY[id] || id;
    if (value.trim()) {
      const valid = validatePlatformUrl(key, value);
      setUrlErrors(prev => ({
        ...prev,
        [id]: valid ? '' : `Please enter a valid ${id.charAt(0).toUpperCase() + id.slice(1)} URL.`,
      }));
    } else {
      setUrlErrors(prev => ({ ...prev, [id]: '' }));
    }
  };

  const handlePlatformUrlBlur = (id: string, value: string) => {
    setUrlDirty(prev => ({ ...prev, [id]: true }));
    const key = PLATFORM_ID_TO_KEY[id] || id;
    if (!value.trim()) {
      setUrlErrors(prev => ({ ...prev, [id]: '' }));
      return;
    }
    const valid = validatePlatformUrl(key, value);
    setUrlErrors(prev => ({
      ...prev,
      [id]: valid ? '' : `Please enter a valid ${id.charAt(0).toUpperCase() + id.slice(1)} URL.`,
    }));
  };

  const handleConnectPlatform = (id: string, code?: string) => {
    setPlatforms(prev => prev.map(p => (p.id === id ? { ...p, connected: true, oauth_code: code } : p)));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next.platforms;
      return next;
    });
  };

  const handleDisconnectPlatform = (id: string) => {
    setPlatforms(prev => prev.map(p => (p.id === id ? { ...p, connected: false, url: '', oauth_code: undefined, instagram_url: undefined, instagram_username: undefined, instagram_checked: undefined } : p)));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next.platforms;
      return next;
    });
  };

  const togglePermission = (key: keyof PermissionState) => {
    setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Step 1 is valid when every connected platform has a non-empty, valid URL
  const isStep1Valid =
    platforms.every(p => {
      if (!p.connected) return true; // unselected platforms don't block
      if (!p.url.trim()) return false;
      const key = PLATFORM_ID_TO_KEY[p.id] || p.id;
      return validatePlatformUrl(key, p.url);
    }) && Object.values(urlErrors).every(e => !e);

  // ---- Step 2 Actions ----
  const toggleTone = (tone: string) => {
    setSelectedTones(prev => {
      if (prev.includes(tone)) {
        return prev.filter(t => t !== tone);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, tone];
    });
  };

  const toggleArchetype = (arch: string) => {
    setSelectedArchetypes(prev => {
      if (prev.includes(arch)) {
        return prev.filter(a => a !== arch);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, arch];
    });
  };

  const movePlatformPriority = useCallback((fromIndex: number, toIndex: number) => {
    setPlatformPriority(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const competitorsRef = useRef(competitors);
  useEffect(() => {
    competitorsRef.current = competitors;
  }, [competitors]);

  const validateCompetitorField = useCallback((index: number, field: keyof CompetitorEntry, value: string, focusVal?: string) => {
    const currentCompetitors = competitorsRef.current;
    if (index >= currentCompetitors.length) return;

    const row = currentCompetitors[index];
    const focus = focusVal !== undefined ? focusVal : row.focus;
    const name = field === 'name' ? value : row.name;
    const url = field === 'url' ? value : row.url;

    const hasName = !!name.trim();
    const hasUrl = !!url.trim();
    const hasPlatform = !!focus;
    const isCompletelyEmpty = !hasName && !hasUrl && !hasPlatform;

    setFieldErrors(prev => {
      const newErrors = { ...prev };
      
      if (isCompletelyEmpty) {
        delete newErrors[`competitors.${index}.name`];
        delete newErrors[`competitors.${index}.url`];
        delete newErrors[`competitors.${index}.focus`];
        return newErrors;
      }

      const errKey = `competitors.${index}.${field === 'focus' ? 'focus' : field}`;
      delete newErrors[errKey];

      if (field === 'name' && !value.trim()) {
        newErrors[errKey] = 'This field is required.';
      }
      if (field === 'focus' && !value) {
        newErrors[errKey] = 'This field is required.';
      }
      if (field === 'url' && !value.trim()) {
        newErrors[errKey] = 'This field is required.';
      }

      if (field === 'url' && value.trim() && focus) {
        const platformKey = mapDropdownToPlatformKey(focus);
        const valid = validatePlatformUrl(platformKey, value.trim());
        if (!valid) {
          let example = '';
          if (platformKey === 'youtube') example = 'https://www.youtube.com/@username';
          else if (platformKey === 'linkedin') example = 'https://www.linkedin.com/company/slug';
          else if (platformKey === 'meta_facebook') example = 'https://www.facebook.com/pagename';
          else if (platformKey === 'meta_instagram') example = 'https://www.instagram.com/username';
          newErrors[errKey] = `This URL doesn't match a valid ${focus} link. Example: ${example}`;
        }
      }

      const canonicalPlatform = mapDropdownToPlatformKey(focus);
      const isDuplicate = currentCompetitors.some((c, idx) => {
        if (idx === index) return false;
        return c.name.toLowerCase().trim() === name.toLowerCase().trim() &&
               mapDropdownToPlatformKey(c.focus) === canonicalPlatform;
      });

      if (isDuplicate && name && focus) {
        newErrors[`competitors.${index}.url`] = `Duplicate entry for ${name} on ${focus}.`;
      } else {
        if (newErrors[`competitors.${index}.url`]?.startsWith('Duplicate entry')) {
          delete newErrors[`competitors.${index}.url`];
        }
      }

      return newErrors;
    });
  }, []);

  const debouncedValidateCompetitorField = useMemo(
    () => debounce(validateCompetitorField, 500),
    [validateCompetitorField]
  );

  const updateCompetitor = (index: number, field: keyof CompetitorEntry, value: string) => {
    setCompetitors(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === 'focus') {
      const currentUrl = competitorsRef.current[index]?.url || '';
      validateCompetitorField(index, 'url', currentUrl, value);
      validateCompetitorField(index, 'focus', value, value);
    } else {
      debouncedValidateCompetitorField(index, field, value);
    }
  };

  const addCompetitor = () => {
    setCompetitors(prev => [...prev, { name: '', url: '', focus: '' }]);
  };

  const removeCompetitor = (index: number) => {
    setCompetitors(prev => {
      if (prev.length <= 1) return prev;
      const updated = prev.filter((_, i) => i !== index);
      
      setFieldErrors(errors => {
        const nextErrors = { ...errors };
        Object.keys(nextErrors).forEach(key => {
          if (key.startsWith('competitors.')) {
            delete nextErrors[key];
          }
        });
        return nextErrors;
      });

      return updated;
    });
  };

  const handleCompetitorBlur = (index: number, field: keyof CompetitorEntry, value: string) => {
    validateCompetitorField(index, field, value);
  };

  const isStep2Valid = useMemo(() => {
    const hasInvalidCompetitor = competitors.some(c => {
      const hasName = !!c.name.trim();
      const hasUrl = !!c.url.trim();
      const hasPlatform = !!c.focus;
      
      const isCompletelyEmpty = !hasName && !hasUrl && !hasPlatform;
      if (isCompletelyEmpty) return false;
      
      if (!hasName || !hasUrl || !hasPlatform) {
        return true;
      }
      return false;
    });

    if (hasInvalidCompetitor) return false;

    const hasCompetitorErrors = Object.keys(fieldErrors).some(key => key.startsWith('competitors.'));
    return !hasCompetitorErrors;
  }, [competitors, fieldErrors]);

  // ---- Step 3 Actions ----
  const setStrategy = (mode: StrategyMode) => {
    setStrategyMode(mode);
    if (mode === 'regular') setAllocationPercent(100);
    else if (mode === 'campaign') setAllocationPercent(0);
    else setAllocationPercent(planCap);
  };

  const toggleObservance = (obs: string) => {
    setObservanceError('');
    setSelectedObservances(prev => {
      if (prev.includes(obs)) return prev.filter(o => o !== obs);
      if (prev.length >= maxObservances) {
        setObservanceError(`Hard limit reached: A maximum of ${maxObservances} observance posts can be chosen`);
        return prev;
      }
      return [...prev, obs];
    });
  };

  // "Select Top N": fill the remaining picks from the given keys (the loaded
  // observances, earliest first).
  const selectAllObservances = (candidateKeys: string[]) => {
    setSelectedObservances(prev => {
      const additions = candidateKeys.filter(k => !prev.includes(k)).slice(0, Math.max(maxObservances - prev.length, 0));
      setObservanceError('');
      return [...prev, ...additions];
    });
  };

  const handleContinue = async () => {
    const userId = localStorage.getItem('user_id') || '';
    setStepError('');
    setFieldErrors({});
    setIsSaving(true);
    try {
      let currentStepFormData: any = null;
      if (currentStep === 1) {
        currentStepFormData = {
          platforms: platforms.map(p => ({
            platform: p.id,
            profile_url: p.url,
            oauth_code: p.oauth_code,
            connected: p.connected,
          })),
          permissions: {
            reach_analytics: permissions.reachAnalytics,
            audience_demographics: permissions.audienceDemographics,
            comment_visibility: permissions.commentVisibility,
            watch_time_subscriptions: permissions.watchTimeSubscriptions,
          }
        };
      } else if (currentStep === 2) {
        currentStepFormData = {
          communication_tones: selectedTones,
          brand_personalities: selectedArchetypes,
          platform_priority_order: platformPriority,
          competitor_references: competitors.map(c => ({
            name: c.name,
            url: c.url,
            platform_type: c.focus,
          })),
        };
      } else if (currentStep === 3) {
        currentStepFormData = {
          planning_approach: strategyMode,
          allocation_percent: allocationPercent,
          observance_enabled: observanceEnabled,
          selected_observances: selectedObservances,
          country: country,
          region: region,
        };
      }

      // Call validation API
      const token = localStorage.getItem('access_token');
      const validateRes = await fetch(`${API_BASE_URL}/smm/onboarding/draft/step/${currentStep}/validate?business_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          step_number: currentStep,
          data: currentStepFormData,
        }),
      });

      if (!validateRes.ok) {
        const errorData = await validateRes.json().catch(() => ({}));
        throw new Error(errorMessageFromBody(errorData, validateRes.status));
      }

      const validationResult = await validateRes.json();
      if (!validationResult.valid) {
        const errors: Record<string, string> = validationResult.errors || {};
        setFieldErrors(errors);
        // Not every step renders fieldErrors inline (Step 3 doesn't), so
        // always surface the first reason in the step error banner too.
        const firstError = Object.values(errors).find(msg => typeof msg === 'string' && msg.trim());
        setStepError(firstError || 'Please fix the highlighted fields.');
        setIsSaving(false);
        return;
      }

      if (currentStep === 1) {
        const response = await saveStep1(userId, { platforms, permissions });
        
        // Update Facebook platform with linked Instagram info. Drop the OAuth
        // codes: the backend has exchanged them, and a code works once — going
        // back and pressing Continue again would re-send it and fail.
        setPlatforms(prev => prev.map(p => ({ ...p, oauth_code: undefined })).map(p => {
          if (p.id === 'facebook') {
            const igAccounts = response.data?.linked_instagram_accounts || [];
            const igAccount = igAccounts.length > 0 ? igAccounts[0] : null;
            return {
              ...p,
              instagram_checked: true,
              instagram_url: igAccount?.profile_url ?? p.instagram_url,
              instagram_username: igAccount?.username ?? p.instagram_username,
            };
          }
          return p;
        }));
        
        setCurrentStep(2);
      } else if (currentStep === 2) {
        await saveStep2(userId, { selectedTones, selectedArchetypes, platformPriority, competitors });
        setCurrentStep(3);
      } else if (currentStep === 3) {
        await saveStep3(userId, { strategyMode, allocationPercent, observanceEnabled, selectedObservances, country, region });
        await completeOnboarding(userId);
        setIsComplete(true);
      }
    } catch (err: any) {
      setStepError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'An error occurred. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // handleBack decrements currentStep only — no API call on back.
  // When the user navigates forward again from a previous step, saveStepN is
  // called again and the backend upserts cleanly (uix_smm_platform_user + UNIQUE
  // user_id constraints make every save idempotent).

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return {
    currentStep,
    isComplete,
    setIsComplete,
    // Step 1
    ecosystemUrl,
    urlScanning,
    urlScanned,
    platforms,
    permissionsExpanded,
    permissions,
    handleEcosystemUrlChange,
    handlePlatformUrlChange,
    handlePlatformUrlBlur,
    handleConnectPlatform,
    handleDisconnectPlatform,
    setPermissionsExpanded,
    togglePermission,
    isStep1Valid,
    // Channel detection
    channelDetection,
    detectionLoading,
    urlErrors,
    urlDirty,
    // Step 2
    selectedTones,
    selectedArchetypes,
    platformPriority,
    competitors,
    toggleTone,
    toggleArchetype,
    movePlatformPriority,
    updateCompetitor,
    addCompetitor,
    removeCompetitor,
    handleCompetitorBlur,
    isStep2Valid,
    // Step 3
    strategyMode,
    allocationPercent,
    setAllocationPercent,
    planCap,
    planName,
    observanceEnabled,
    selectedObservances,
    setSelectedObservances,
    observanceError,
    country,
    setCountry,
    region,
    setRegion,
    setStrategy,
    setObservanceEnabled,
    toggleObservance,
    maxObservances,
    setMaxObservances,
    setObservanceError,
    selectAllObservances,
    // Navigation
    handleContinue,
    handleBack,
    setCurrentStep,
    // API state
    stepError,
    isSaving,
    isStatusLoading,
    fieldErrors,
    loadError,
    retryLoad,
    draftSaveFailed,
  };
}